import type {
  LocationFormProps,
  LocationFormValues,
} from "./location-form.type";
import type { LocationMutateVariables } from "@/entities/location";

import { Button, Divider, Form, Input, Space, Typography } from "antd";
import { useTranslation } from "react-i18next";

import Coordinates from "@/components/Form/Coordinates";
import { isSector as isSectorFn } from "@/entities/location";
import { useOnFormClose, useGenericFormErrorNotification } from "@/hooks/forms";
import { useChain } from "@/hooks/useChain";
import DocumentUploadField, {
  getSingleDocumentUploadFieldValue,
} from "@/modules/document/document-upload-field";
import { useFormValidation } from "@/modules/form-validation";
import { GeojsonEditor } from "@/modules/geojson-editor";
import LocationSelectParent from "@/modules/location/location-select-parent";
import { useOrganizationContextId } from "@/modules/organization/organization-context";
import { getSelectSingleValue } from "@/modules/select";
import { SidePanelFooter } from "@/modules/side-panel";

const { Title } = Typography;
const { useForm } = Form;

const LocationForm = ({
  location,
  isSector,
  mutation,
  saveButtonLabel,
  initialValues,
  onMutate,
}: LocationFormProps) => {
  const [form] = useForm();
  const { t } = useTranslation();
  const { contextId } = useOrganizationContextId();

  const isSectorMode = isSector ?? isSectorFn(location);
  const translationKey = isSectorMode ? "sector" : "location";

  const onClose = useOnFormClose(form);
  const onMutateSuccess = useChain([onClose, onMutate]);
  useGenericFormErrorNotification(mutation.isError);
  const { validationErrors, validation } = useFormValidation(
    mutation.isError,
    mutation.error,
    [
      "name",
      "description",
      "coordinate",
      "organization_id",
      "location_id",
      "geographical_data",
    ]
  );

  const onSave = (formValues: LocationFormValues) => {
    const data: LocationMutateVariables = {
      name: form.getFieldValue("name"),
      description: form.getFieldValue("description"),
      coordinate: form.getFieldValue("coordinate"),
      organization_id: contextId,
      geographical_data: form.getFieldValue("geojson"),
      photo: getSingleDocumentUploadFieldValue({
        formValue: formValues.photo,
        defaultDocument: location?.photo,
      }),
      location_id: getSelectSingleValue(form, "location") || null,
    };
    mutation.mutate(data, { onSuccess: onMutateSuccess });
  };

  return (
    <Form
      id="locationForm"
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFinish={onSave}
      role="form"
    >
      <Title level={3}>
        {t(`${translationKey}:form.associateLocationWith`)}
      </Title>
      <LocationSelectParent
        currentLocationId={location?.id}
        {...validation("location_id")}
      />
      <Divider />
      <Title level={3}>{t(`${translationKey}:form.informations`)}</Title>
      <Form.Item
        name="name"
        label={t("location:form.name.label")}
        rules={[{ required: true }]}
        {...validation("name")}
      >
        <Input placeholder={t("location:form.name.label")} data-testid="name" />
      </Form.Item>
      <Form.Item
        name="description"
        label={t("location:form.description.label")}
        {...validation("description")}
      >
        <Input
          placeholder={t("location:form.description.label")}
          data-testid="description"
        />
      </Form.Item>
      <Form.Item
        name="coordinate"
        label={t("global:coordinate.label")}
        rules={[{ required: true }]}
        {...validation("coordinate")}
      >
        <Coordinates
          mapId={`location_form_${location?.id}`}
          allowClear={true}
          sizes={{ height: 200 }}
          onCoordinateChange={(lat, lng) =>
            form.setFieldsValue({ coordinate: `${lat},${lng}` })
          }
          onClear={() => form.setFieldsValue({ coordinate: null })}
        />
      </Form.Item>

      <GeojsonEditor {...validation("geographical_data")} />

      <DocumentUploadField
        name="photo"
        label={t("location:form.photo.label")}
        document={location?.photo}
        uploadProps={{ type: "image", translationType: "photo" }}
        {...validation("photo")}
      />

      {validationErrors().others && <Form.Item {...validation("others")} />}

      <SidePanelFooter>
        <Space>
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            htmlType="submit"
            form="locationForm"
          >
            {saveButtonLabel}
          </Button>
        </Space>
      </SidePanelFooter>
    </Form>
  );
};

export default LocationForm;
